import { apiRequest } from './api-client';

/**
 * Phone notifications for new orders (Web Push, migration 0100) — the browser side.
 *
 * `enablePush` asks the browser's permission, subscribes this device with the platform's VAPID public key through
 * the service worker (`/sw.js`), and hands the subscription to the API; from then on a new order reaches the
 * device's notification bar even with the dashboard closed. `disablePush` undoes both halves.
 *
 * Where it works: Android (Chrome, Edge, Firefox, Samsung Internet) and computers directly; iPhone/iPad (iOS 16.4+)
 * only once the dashboard is added to the home screen — `pushSupport()` says which case applies.
 */

export type PushSupport =
  | 'supported'
  | /** iPhone/iPad in Safari: add the dashboard to the home screen first. */ 'needs-install'
  | 'unsupported';

interface Scope {
  readonly accessToken: string | null;
  readonly shopId: string | null;
}

/** Remembered on this device so the page does not also show its own notification while push is on. */
export const PUSH_FLAG_KEY = 'souk-push-on';

function isIos(): boolean {
  // An iPad in desktop mode reports itself as a Mac: the touch screen gives it away.
  return (
    /iPad|iPhone|iPod/u.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)
  );
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function pushSupport(): PushSupport {
  if (typeof window === 'undefined') return 'unsupported';
  const capable =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (isIos() && !isStandalone()) return 'needs-install';
  return capable ? 'supported' : 'unsupported';
}

function setFlag(on: boolean): void {
  try {
    if (on) localStorage.setItem(PUSH_FLAG_KEY, '1');
    else localStorage.removeItem(PUSH_FLAG_KEY);
  } catch {
    // Storage unavailable: at worst the page shows its own notification too.
  }
}

export function pushFlagOn(): boolean {
  try {
    return localStorage.getItem(PUSH_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

/** The VAPID key as the bytes `PushManager.subscribe` expects (it is base64url text). */
export function vapidKeyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = `${base64Url}${'='.repeat((4 - (base64Url.length % 4)) % 4)}`
    .replace(/-/gu, '+')
    .replace(/_/gu, '/');
  const raw = atob(padded);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  return existing ?? navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

/** The platform's public key, or null when phone notifications are not configured on the server. */
export async function pushPublicKey(scope: Scope): Promise<string | null> {
  const { publicKey } = await apiRequest<{ publicKey: string | null }>(
    '/merchant/push/config',
    scope,
  );
  return publicKey;
}

/** Whether THIS device currently receives the shop's new orders. */
export async function pushEnabled(scope: Scope): Promise<boolean> {
  if (pushSupport() !== 'supported' || Notification.permission !== 'granted') return false;
  const existing = await navigator.serviceWorker.getRegistration('/');
  const subscription = await existing?.pushManager.getSubscription();
  if (subscription === undefined || subscription === null) return false;
  const { subscribed } = await apiRequest<{ subscribed: boolean }>(
    '/merchant/push/subscription/status',
    { ...scope, method: 'POST', body: { endpoint: subscription.endpoint } },
  );
  setFlag(subscribed);
  return subscribed;
}

/** Returns the permission outcome: anything but 'granted' leaves the device unsubscribed. */
export async function enablePush(scope: Scope, publicKey: string): Promise<NotificationPermission> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;
  const worker = await registration();
  await navigator.serviceWorker.ready;
  const subscription =
    (await worker.pushManager.getSubscription()) ??
    (await worker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKeyBytes(publicKey),
    }));
  const json = subscription.toJSON();
  await apiRequest('/merchant/push/subscription', {
    ...scope,
    method: 'PUT',
    body: {
      endpoint: subscription.endpoint,
      keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
      userAgent: navigator.userAgent.slice(0, 300),
    },
  });
  setFlag(true);
  return permission;
}

export async function disablePush(scope: Scope): Promise<void> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  const subscription = await existing?.pushManager.getSubscription();
  if (subscription !== undefined && subscription !== null) {
    await apiRequest('/merchant/push/subscription', {
      ...scope,
      method: 'DELETE',
      body: { endpoint: subscription.endpoint },
    });
    await subscription.unsubscribe();
  }
  setFlag(false);
}
